'use client'

import { ESHOP_COOKIE_CONSENT_NAME } from '@/eshop/constants'
import { getCookieOnClient } from '@/utils/cookie'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Cookie from 'js-cookie'
import { z } from 'zod'
import Modal from '../Modal'

const cookieValue = z.union([
    z.literal('tech'),
    z.literal('analytics'),
    z.literal('ads'),
])
type CookieValue = z.infer<typeof cookieValue>

export default function Cookies() {
    const [show, setShow] = useState(false)
    const [showModal, setShowModal] = useState(false)
    const [allowed, setAllowed] = useState<CookieValue[]>(['tech'])

    const { t } = useTranslation()

    useEffect(() => {
        const isCookieAllowed = getCookieOnClient(ESHOP_COOKIE_CONSENT_NAME)
        if (isCookieAllowed === undefined) {
            setShow(true)
        }
        try {
            const isValid = isCookieAllowed
                ?.split(',')
                .map((val) => cookieValue.safeParse(val))
                .every((val) => val.success)
            if (!isValid) {
                Cookie.remove(ESHOP_COOKIE_CONSENT_NAME)
                setShow(true)
            }
        } catch (e) {
            setShow(true)
        }
    }, [])

    const handleAllowCookies = useCallback((allowed: CookieValue[]) => {
        Cookie.set(ESHOP_COOKIE_CONSENT_NAME, allowed.join(','))
        setShow(false)
    }, [])

    if (!show) {
        return null
    }

    return (
        <div
            className="p-6 fixed bottom-0 left-0 w-full bg-white flex justify-center"
            style={{
                boxShadow: 'rgba(0, 0, 0, 0.15) 0px 0px 10px',
            }}
        >
            <div className="w-full max-w-4xl">
                {t('eshop.cookies.text')}
                <div className="mt-2 flex justify-center gap-4">
                    <button
                        className="btn btn-sm btn-neutral text-white py-0"
                        onClick={() => setShowModal(true)}
                    >
                        {t('eshop.cookies.settingsBtn')}
                    </button>
                    <button
                        className="btn btn-sm btn-primary py-0"
                        onClick={() =>
                            handleAllowCookies(['tech', 'ads', 'analytics'])
                        }
                    >
                        {t('eshop.cookies.allowBtn')}
                    </button>
                </div>
            </div>
            <Modal show={showModal} onHide={() => setShowModal(false)}>
                <div className="flex flex-col gap-4">
                    <div className="flex gap-4">
                        <div className="w-9/12">
                            <span className="text-md font-bold">
                                {t('eshop.cookies.techTitle')}
                            </span>
                            <p>{t('eshop.cookies.techDescription')}</p>
                        </div>
                        <div>
                            <input
                                type="checkbox"
                                className="toggle"
                                checked
                                disabled
                            />
                        </div>
                    </div>
                    <div className="flex gap-4">
                        <div className="w-9/12">
                            <span className="text-md font-bold">
                                {t('eshop.cookies.analyticsTitle')}
                            </span>
                            <p>{t('eshop.cookies.analyticsDescription')}</p>
                        </div>
                        <div>
                            <input
                                type="checkbox"
                                className="toggle"
                                checked={allowed.includes('analytics')}
                                onChange={(evt) => {
                                    if (evt.target.checked) {
                                        setAllowed((state) => [
                                            ...state,
                                            'analytics',
                                        ])
                                    } else {
                                        setAllowed((state) =>
                                            state.filter(
                                                (s) => s !== 'analytics'
                                            )
                                        )
                                    }
                                }}
                            />
                        </div>
                    </div>
                    <div className="flex gap-4">
                        <div className="w-9/12">
                            <span className="text-md font-bold">
                                {t('eshop.cookies.adsTitle')}
                            </span>
                            <p>{t('eshop.cookies.adsDescription')}</p>
                        </div>
                        <div>
                            <input
                                type="checkbox"
                                className="toggle"
                                checked={allowed.includes('ads')}
                                onChange={(evt) => {
                                    if (evt.target.checked) {
                                        setAllowed((state) => [...state, 'ads'])
                                    } else {
                                        setAllowed((state) =>
                                            state.filter((s) => s !== 'ads')
                                        )
                                    }
                                }}
                            />
                        </div>
                    </div>
                </div>
                <div className="flex gap-4 items-center justify-center pt-10">
                    <button
                        className="btn btn-sm btn-info text-white"
                        onClick={() => {
                            handleAllowCookies(allowed)
                        }}
                    >
                        {t('eshop.cookies.allowSelectedBtn')}
                    </button>
                    <button
                        className="btn btn-sm btn-success text-white"
                        onClick={() => {
                            handleAllowCookies(['tech', 'analytics', 'ads'])
                        }}
                    >
                        {t('eshop.cookies.allowAllBtn')}
                    </button>
                </div>
            </Modal>
        </div>
    )
}
